import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, ExternalLink, ShieldCheck, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../api";

const STATUS_STYLE = {
  Operational: "bg-gold-soft/70 text-pine-deep",
  Active: "bg-teal-soft text-teal-brand",
  "Opening Soon": "bg-amber-50 text-amber-600",
};

export default function MultiClinic() {
  const nav = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ owner: "", city: "", state: "" });
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const isSuper = me.level === "super_admin";
  const [current, setCurrent] = useState(localStorage.getItem("dyntra_connect_clinic") || "Dyntra Clinics by Dr. Ankita, Noida");

  const load = () => api.get("/connect/clinics").then((r) => setClinics(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addClinic = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/connect/clinics", addForm);
      toast.success(`Clinic created: ${data.name}, ${data.city}`);
      setAddForm({ owner: "", city: "", state: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create clinic");
    }
  };

  const openDashboard = (c) => {
    localStorage.setItem("dyntra_connect_clinic", `${c.name}, ${c.city}`);
    localStorage.setItem("dyntra_connect_clinic_id", c.id);
    setCurrent(`${c.name}, ${c.city}`);
    toast.success(`Now viewing the dashboard as ${c.name}, ${c.city} — patients, appointments and revenue are now this clinic's`);
    window.dispatchEvent(new Event("clinic-changed"));
    nav("/connect/app");
  };

  if (!isSuper) {
    return (
      <div data-testid="connect-multiclinic" className="max-w-xl rounded-3xl bg-white border border-pine/8 shadow-soft p-10 text-center">
        <Building2 className="h-10 w-10 mx-auto text-slate-300" />
        <h2 className="mt-4 font-display text-2xl font-semibold text-pine-deep">Multi-Clinic</h2>
        <p className="mt-2 text-sm text-slate-500">Cross-clinic access is available to the Super Admin role.</p>
      </div>
    );
  }

  return (
    <div data-testid="connect-multiclinic" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Multi-Clinic Network</h2>
          <p className="text-sm text-slate-500">Every clinic running Dyntra Connect. Open any clinic's dashboard with one click.</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-pine-deep text-gold px-4 py-2 text-xs font-bold">
          <ShieldCheck className="h-4 w-4" /> Super Admin · Viewing: {current}
        </span>
        <button data-testid="add-clinic-toggle" onClick={() => setShowAdd(!showAdd)} className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-4 py-2 text-xs font-bold text-white hover:bg-pine transition-all">
          <Plus className="h-4 w-4" /> Add Clinic
        </button>
      </div>

      {showAdd && (
        <form data-testid="add-clinic-form" onSubmit={addClinic} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-4 gap-3">
          <input required data-testid="clinic-owner" placeholder="Doctor name (e.g. Dr. Neha Gupta)" value={addForm.owner} onChange={(e) => setAddForm({ ...addForm, owner: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input required data-testid="clinic-city" placeholder="City" value={addForm.city} onChange={(e) => setAddForm({ ...addForm, city: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input required data-testid="clinic-state" placeholder="State" value={addForm.state} onChange={(e) => setAddForm({ ...addForm, state: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <button data-testid="clinic-submit" className="h-11 rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">Create Clinic</button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {clinics.map((c) => (
          <div key={c.id} data-testid={`clinic-card-${c.id}`} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 hover:-translate-y-0.5 hover:shadow-lift transition-all">
            <div className="flex items-start justify-between gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream text-pine"><Building2 className="h-5 w-5" /></span>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${STATUS_STYLE[c.status] || STATUS_STYLE["Opening Soon"]}`}>{c.status}</span>
            </div>
            <p className="mt-3 font-bold text-pine-deep leading-snug">{c.name}</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5 text-gold" /> {c.city}, {c.state}</p>
            <p className="mt-1 text-xs text-slate-400">by {c.owner}</p>
            <button
              data-testid={`clinic-open-${c.id}`}
              onClick={() => openDashboard(c)}
              className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold transition-all ${
                current.startsWith(c.name) ? "bg-teal-brand text-white" : "bg-pine text-ivory hover:bg-pine-deep"
              }`}
            >
              <ExternalLink className="h-3.5 w-3.5" /> {current.startsWith(c.name) ? "Currently Viewing" : "Open Dashboard"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
